import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import { api } from "@/lib/api";

const getActivity = () => api.get("/liquidity/activity", { params: { limit: 15 } }).then((r) => r.data);
const fmt = (v, d) => Number(v).toLocaleString(undefined, { maximumFractionDigits: d });
const short = (a) => `${a.slice(0, 6)}…${a.slice(-4)}`;
const ago = (ts) => {
  if (!ts) return "";
  const s = Math.max(Math.floor(Date.now() / 1000 - ts), 0);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const KIND = {
  deposit: { label: "Deposit", icon: ArrowDownToLine, tone: "text-green-400 bg-green-500/10" },
  withdraw: { label: "Withdraw", icon: ArrowUpFromLine, tone: "text-amber-300 bg-amber-500/10" },
};

const ActivityRow = ({ e, i }) => {
  const k = KIND[e.kind];
  const Icon = k.icon;
  return (
    <li data-testid={`lp-activity-row-${i}`} className="flex items-center gap-3 py-3 border-b border-[#2A2411] last:border-0">
      <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${k.tone}`}><Icon size={14} /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-white">
          <span className="font-medium">{k.label}</span>{" "}
          <span className="font-mono text-zinc-300">{fmt(e.fmi, 4)} FMI + {fmt(e.usdt, 2)} USDT</span>
        </p>
        <p className="text-xs text-zinc-500">
          by <Link to={`/address/${e.provider}`} data-testid={`lp-activity-provider-${i}`} className={e.providerLabel ? "font-medium text-[#D4AF37] hover:underline" : "font-mono hover:text-[#D4AF37]"}>
            {e.providerLabel || short(e.provider)}
          </Link> · {ago(e.timestamp)}
        </p>
      </div>
      <Link to={`/tx/${e.txHash}`} data-testid={`lp-activity-tx-${i}`} className="text-xs font-mono text-[#D4AF37] hover:underline shrink-0">{e.txHash.slice(0, 10)}…</Link>
    </li>
  );
};

export const PoolActivity = () => {
  const { data, isError } = useQuery({ queryKey: ["lpActivity"], queryFn: getActivity, refetchInterval: 15000 });
  const events = data?.events || [];
  return (
    <div data-testid="lp-activity" className="p-5 rounded-xl border border-[#2A2411] bg-[#0A0A0A]">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-white">Recent pool activity</p>
        {data && <span data-testid="lp-activity-total" className="text-xs text-zinc-500">{data.total} deposits &amp; withdrawals</span>}
      </div>
      {isError && <p className="text-sm text-red-400">Could not load pool activity</p>}
      {data && events.length === 0 && <p data-testid="lp-activity-empty" className="text-sm text-zinc-500 py-3">No deposits or withdrawals yet. Be the first provider.</p>}
      <ul>{events.map((e, i) => <ActivityRow key={e.txHash} e={e} i={i} />)}</ul>
    </div>
  );
};
